import { useSelector } from "@xstate/react";

import { type EncounterStatus, encounterActor } from "../domain";

export function useEncounterId() {
  return useSelector(encounterActor, (s) => s.context.encounterId);
}

/**
 * `completedAt` baseline captured when finish was requested for the active
 * encounter. Null when no finish attempt is in flight.
 */
export function useEncounterCompletedAtBaseline(): string | null {
  return useSelector(encounterActor, (s) => s.context.completedAtBaseline);
}

export function useEncounterStatusValue(): EncounterStatus {
  return useSelector(encounterActor, (s) => s.value as EncounterStatus);
}

export function useIsEncounterCompleted(): boolean {
  return useSelector(
    encounterActor,
    (s) => s.matches("completed_awaiting_note") || s.matches("completed"),
  );
}

export function useIsEncounterFinalizing(): boolean {
  return useSelector(encounterActor, (s) => s.matches("finalizing"));
}

/**
 * Whether the encounter actor is in the client finish-intent state
 * (`finish_requested`), registered before navigation / SSE finalizing.
 */
export function useIsEncounterFinishRequested(): boolean {
  return useSelector(encounterActor, (s) => s.matches("finish_requested"));
}

/**
 * Whether the encounter lifecycle is waiting on note generation (client finish
 * intent, server/SSE finalizing, or server-completed but note not yet observed).
 */
export function useIsEncounterNoteGenerationActive(): boolean {
  return useSelector(
    encounterActor,
    (s) =>
      s.matches("finish_requested") ||
      s.matches("finalizing") ||
      s.matches("completed_awaiting_note"),
  );
}

/**
 * Whether patient chrome already handed finish off to the visit route.
 */
export function useIsEncounterFinishHandedOff(): boolean {
  return useSelector(encounterActor, (s) => s.context.finishHandedOff);
}

export function useIsEncounterConnected(): boolean {
  return useSelector(encounterActor, (s) => !s.matches("idle"));
}
